/** @jsx jsx */
import React, { useMemo, type ReactNode } from 'react';
import { graphql, useFragment, type PreloadedQuery } from 'react-relay';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { GlobalSideNavigation } from '@atlassian/global-side-navigation/GlobalSideNavigation';
import { SidebarSelector } from '@jira/navigation-apps__sidebar-nav4-sidebar-selector/src/ui/regular/index.tsx';
import { Nav4JiraSideNavRecommendation } from '@jira/growth__side-nav-recommendation/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { GSNQueryPreloaderQuery } from './__generated__/GSNQueryPreloaderQuery.graphql.ts';
import { GSNQueryPreloader } from './GSNQueryPreloader.tsx';
import { useNetworkStorageParams } from './useNetworkStorageParams.tsx';
import { SIDEBAR_TEST_ID } from './sidebarTestId.constants.tsx';
import type { GSNForOtherSidebars$key } from './__generated__/GSNForOtherSidebars.graphql.ts';

const styles = cssMap({
	root: {
		marginTop: token('space.negative.150'),
		marginRight: token('space.negative.150'),
		marginBottom: token('space.negative.150'),
		marginLeft: token('space.negative.150'),
	},
});

const EMPTY_CONFIG_OBJ = {} as const;

interface Props {
	queryReference: PreloadedQuery<GSNQueryPreloaderQuery>;
}

export function GSNForOtherSidebars(props: Props): React.JSX.Element {
	return <GSNQueryPreloader queryReference={props.queryReference} render={renderFn} />;
}

function renderFn(params: { queryReference: GSNForOtherSidebars$key }): React.JSX.Element {
	return <GSNForOtherSidebarsInner queryReference={params.queryReference} />;
}

interface InnerProps {
	queryReference: GSNForOtherSidebars$key;
	postMainElement?: ReactNode;
}

// Export this, as it is used by the EP-sidebars and in tests
export function GSNForOtherSidebarsInner(props: InnerProps): React.JSX.Element | null {
	const {
		queryReference,
		postMainElement = (
			<div css={[styles.root]}>
				<SidebarSelector />
			</div>
		),
	} = props;

	// eslint-disable-next-line @atlassian/relay/use-fragment-naming
	const data = useFragment(
		// Note: The parent query has already requested both the menu data and the sidebar data.
		//  Even if we could just indicate that we only need the sidebar data (which we can't,
		//  because GSN expects all of the data), we have already requested it.
		// Need to disable this rule for fragments that are used by child components
		/* eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
		/* eslint-disable @atlassian/relay/unused-fields */
		graphql`
			fragment GSNForOtherSidebars on Query {
				...useNetworkSideNavUserData # This is need for the networkStorageParams type to be happy
				...useGlobalShortcutsDataRelay
					@dangerously_unaliased_fixme
					@include(if: $fetchGlobalShortcutsAvailableProducts)
				navPlatL1MenuData: settings_navigationCustomisation(
					entityAri: $navPlatMenuDataEntityAri
					ownerAri: $ownerAri
				) {
					...useNavSettingsService
				}
				navPlatSidebarData: settings_navigationCustomisation(
					entityAri: $navPlatSidebarDataEntityAri
					ownerAri: $ownerAri
				) {
					...useNavSettingsService
				}
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		queryReference,
	);
	const networkStorageParamsPartial = useNetworkStorageParams();
	const networkStorageParams = useMemo(() => {
		return { ...networkStorageParamsPartial, queryRef: data };
	}, [data, networkStorageParamsPartial]);

	// Note: When Jira GSN adoption rolls out, it should rollout with "network-migration" for 3 months
	//  to give time for all old user settings to be stored in the new records.
	//
	//    - customizationStore="network-migration"
	//
	//  After 3 months of being rolled-out, create a new FG to:
	//   - Change customizationStore to "network"
	//   - @include(if: $fgVariable) for useNetworkSideNavUserData.
	//   - After that has rolled out,
	//
	const customizationStore = 'network';

	return (
		<GlobalSideNavigation
			app="jira"
			appLabel="Jira"
			customizationStore={customizationStore}
			disableMenuCustomization
			forYou={EMPTY_CONFIG_OBJ}
			items={EMPTY_CONFIG_OBJ}
			networkStorageParams={networkStorageParams}
			postMainElem={postMainElement}
			testId={SIDEBAR_TEST_ID}
			{...(fg('gsn-jira-side-nav-in-footer') && {
				bottomElem: <Nav4JiraSideNavRecommendation />,
			})}
		/>
	);
}
